import React, { useEffect } from 'react';
import { useSiteNotificationTools } from '@app/site/secured/rateshopper/rates/provider/useSiteNotificationTools';
import SiteNotification from '@app/model/site_notification/SiteNotification';
import SiteNotificationType from '@app/model/site_notification/SiteNotificationType';
import Message from '@app/component/message/Message';
import DataProvider from '@app/model/_common/DataProvider';
import CompanyHotelParsingSettingState from '@app/model/company_hotel/CompanyHotelParsingSettingState';
import { findProviderData } from '@app/component/provider/FindProviderData';
import MarketStateExportService from '@app/model/market_state/_export/service/MarketStateExportService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { MarketStateExportUtilities } from '@app/site/secured/rateshopper/rates/market-state-export/MarketStateExportPage';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import _isEmpty from 'lodash/isEmpty';
import Tag from '@app/component/tag/Tag';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MarketStateExportReportExecution from '@app/model/market_state/_export/MarketStateExportReportExecution';
import { ReportExecutionState } from '@app/model/market_state/_export/ReportExecution';
import CompanyHotelService from '@app/model/company_hotel/service/CompanyHotelService';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';
import ParityExportService from '@app/model/parity/_export/service/ParityExportService';
import ParityExportReportExecution from '@app/model/parity/_export/ParityExportReportExecution';
import { ParityExportReportPeriod } from '@app/model/parity/_export/ParityExportReport';

type ExportMarketStateNotificationProperties = {
	reportExecutionId: number | string,
	state: ReportExecutionState,
};

type ActivateProviderNotificationState = {
	provider: DataProvider,
	state: CompanyHotelParsingSettingState
};

const SiteNotificationFragment = React.memo(() => {

	const siteNotificationsTools = useSiteNotificationTools();

	if (!_isEmpty(siteNotificationsTools.notificationsPerType)) {

		const providerActivationSiteNotifications = siteNotificationsTools.notificationsPerType.get(SiteNotificationType.ACTIVATE_PROVIDER);
		const exportMarketStateSiteNotifications = siteNotificationsTools.notificationsPerType.get(SiteNotificationType.EXPORT_MARKET_STATE);
		const exportParitySiteNotifications = siteNotificationsTools.notificationsPerType.get(SiteNotificationType.EXPORT_PARITY);

			return <div className="mb-6">
				{!_isEmpty(providerActivationSiteNotifications) &&
					<RenderActivateProviderNotifications
						notifications={providerActivationSiteNotifications!}
						acknowledgeAll={siteNotificationsTools.acknowledgeAll}
					/>}
				{!_isEmpty(exportMarketStateSiteNotifications) &&
					<RenderExportMarketStateNotifications
						notifications={exportMarketStateSiteNotifications!}
						acknowledgeAll={siteNotificationsTools.acknowledgeAll}
					/>}
				{!_isEmpty(exportParitySiteNotifications) &&
					<RenderExportParityNotifications
						notifications={exportParitySiteNotifications!}
						acknowledgeAll={siteNotificationsTools.acknowledgeAll}
					/>}
		</div>;

	}
	return <div />;
});

const RenderActivateProviderNotifications: React.FunctionComponent<{
	notifications: SiteNotification[],
	acknowledgeAll: (ids: number[]) => void
}> = ({ notifications, acknowledgeAll }) => {

	const { companyHotel } = useSelectedCompanyHotel()!;
	const siteNotificationsTools = useSiteNotificationTools();
	const map: Map<DataProvider, ActivateProviderNotificationState> = new Map();

	notifications.forEach(n => {
		if (!map.has(n.properties.provider)) {
			map.set(n.properties.provider, {
				provider: n.properties.provider,
				state: CompanyHotelParsingSettingState.PENDING,
			});
		}
		if (n.properties.state === CompanyHotelParsingSettingState.ACTIVE) {
			map.set(n.properties.provider, {
				provider: n.properties.provider,
				state: CompanyHotelParsingSettingState.ACTIVE,
			});
		}
	});

	if (_isEmpty(map)) return null;

	return <Message state="info" onDismiss={() => acknowledgeAll(notifications.map(n => n.id))}
									title="The below chanel(s) is currently in process to be enabled. You will be notified when they are ready to be used.">
			<div className="mt-2 text-sm text-blue-700">
				<ul role="list" className="list-disc space-y-1 pl-5">
					{Array.from(map).map(([provider, notificationState]) =>
						<RenderActivateProviderNotificationItem
							key={provider}
							companyHotelId={companyHotel.id}
							notificationState={notificationState}
							onStateChange={siteNotificationsTools.refetch}
						/>)}
				</ul>
			</div>
		</Message>;
	};

const RenderActivateProviderNotificationItem: React.FunctionComponent<{
	companyHotelId: number,
	notificationState: ActivateProviderNotificationState,
	onStateChange: () => void
}> = ({ companyHotelId, notificationState, onStateChange }) => {

	const initialState = notificationState.state;
	const provider = notificationState.provider;
	const isInitialStatePending = initialState === CompanyHotelParsingSettingState.PENDING || initialState === CompanyHotelParsingSettingState.PROCESSING;
	const hasNotifiedStateChange = React.useRef(false);
	const queryClient = useQueryClient();

	const companyHotelQuery = useQuery<CompanyHotel>({
		queryKey: ['site-notification-activate-provider', companyHotelId, provider],
		queryFn: () => CompanyHotelService.findCompanyHotelWithCompetitors(companyHotelId),
		enabled: isInitialStatePending,
		refetchInterval: (query) => {
			const state = findCurrentProviderState(query.state.data, provider, initialState);
			return state === CompanyHotelParsingSettingState.PENDING || state === CompanyHotelParsingSettingState.PROCESSING ? 5000 : false;
		},
		refetchOnWindowFocus: false,
	});

	const currentState = findCurrentProviderState(companyHotelQuery.data, provider, initialState);

	useEffect(() => {
		if (isInitialStatePending
			&& !hasNotifiedStateChange.current
			&& currentState !== CompanyHotelParsingSettingState.PENDING
			&& currentState !== CompanyHotelParsingSettingState.PROCESSING) {
			hasNotifiedStateChange.current = true;
			if (currentState === CompanyHotelParsingSettingState.ACTIVE) {
				if (companyHotelQuery.data) {
					queryClient.setQueryData(CompanyHotelQueryKeys.detail(companyHotelId), companyHotelQuery.data);
				}
				queryClient.invalidateQueries({ queryKey: CompanyHotelQueryKeys.detail(companyHotelId) });
				queryClient.invalidateQueries({ queryKey: CompanyHotelQueryKeys.list() });
			}
			onStateChange();
		}
	}, [companyHotelId, companyHotelQuery.data, currentState, isInitialStatePending, onStateChange, queryClient]);

	return <li>
		<span className="mr-3">{findProviderData(provider).name}</span>
		{(currentState === CompanyHotelParsingSettingState.PENDING || currentState === CompanyHotelParsingSettingState.PROCESSING) &&
			<Tag state="warning">{currentState === CompanyHotelParsingSettingState.PROCESSING ? 'Processing' : 'Pending'}</Tag>}
		{currentState === CompanyHotelParsingSettingState.ACTIVE && <Tag state="success">Active</Tag>}
	</li>;
};

const findCurrentProviderState = (
	companyHotel: CompanyHotel | undefined,
	provider: DataProvider,
	fallbackState: CompanyHotelParsingSettingState,
): CompanyHotelParsingSettingState => {
	const parsingSettings = companyHotel?.findParsingSettingsForProvider(provider);
	if (!parsingSettings || _isEmpty(parsingSettings)) return fallbackState;
	if (parsingSettings.some(item => item.state === CompanyHotelParsingSettingState.ACTIVE)) return CompanyHotelParsingSettingState.ACTIVE;
	if (parsingSettings.some(item => item.state === CompanyHotelParsingSettingState.PROCESSING)) return CompanyHotelParsingSettingState.PROCESSING;
	if (parsingSettings.some(item => item.state === CompanyHotelParsingSettingState.PENDING)) return CompanyHotelParsingSettingState.PENDING;
	return parsingSettings[0].state;
};

const resolveReportExecutionId = (properties: ExportMarketStateNotificationProperties): number | undefined => {
	const reportExecutionId = properties.reportExecutionId;

	if (typeof reportExecutionId === 'number') return reportExecutionId;

	const parsedReportExecutionId = Number(reportExecutionId);
	return Number.isFinite(parsedReportExecutionId) ? parsedReportExecutionId : undefined;
};

const RenderExportMarketStateNotifications: React.FunctionComponent<{
	notifications: SiteNotification[],
	acknowledgeAll: (ids: number[]) => void
}> = ({ notifications, acknowledgeAll }) => {

	const { companyHotel } = useSelectedCompanyHotel()!;
	const toaster = useToaster();
	const siteNotificationsTools = useSiteNotificationTools();

	const download = (reportExecutionId: number) => {
		MarketStateExportService.findCompanyHotelMarketStateExportReportExecution(companyHotel.id, reportExecutionId)
			.then(exp => MarketStateExportUtilities.handleDownload(exp))
			.catch(() => toaster.error('Download Market State Report', 'Error downloading export. Please try again later!'));
	};

	const map: Map<number, ExportMarketStateNotificationProperties> = new Map();

	notifications.forEach(n => {
		const reportExecutionId = resolveReportExecutionId(n.properties);
		if (reportExecutionId !== undefined) {
			map.set(reportExecutionId, n.properties);
		}
	});

	if (_isEmpty(map)) return null;

	return <Message state="info" onDismiss={() => acknowledgeAll(notifications.map(n => n.id))}
								title="The below reports are in the process to be created. You will be notified when they are ready to be downloaded.">
			<div className="mt-2 text-sm text-blue-700">
				<ul role="list" className="list-disc space-y-1 pl-5">
					{Array.from(map).map(([reportExecutionId, properties]) =>
						<RenderExportMarketStateNotificationItem
							key={reportExecutionId}
							companyHotelId={companyHotel.id}
							properties={properties}
							download={download}
							onStateChange={siteNotificationsTools.refetch}
						/>)}
				</ul>
			</div>
		</Message>;
};

const RenderExportMarketStateNotificationItem: React.FunctionComponent<{
	companyHotelId: number,
	properties: ExportMarketStateNotificationProperties,
	download: (reportExecutionId: number) => void,
	onStateChange: () => void
}> = ({ companyHotelId, properties, download, onStateChange }) => {

	const initialState: ReportExecutionState = properties.state;
	const reportExecutionId = resolveReportExecutionId(properties)!;
	const isInitialStatePending = initialState === ReportExecutionState.NEW || initialState === ReportExecutionState.IN_PROCESS;
	const hasNotifiedStateChange = React.useRef(false);

	const exportStateQuery = useQuery<MarketStateExportReportExecution>({
		queryKey: ['site-notification-market-state-export-execution', companyHotelId, reportExecutionId],
		queryFn: () => MarketStateExportService.findCompanyHotelMarketStateExportReportExecution(companyHotelId, reportExecutionId),
		enabled: isInitialStatePending,
		refetchInterval: (query) => {
			const state = query.state.data?.execution.state ?? initialState;
			return state === ReportExecutionState.NEW || state === ReportExecutionState.IN_PROCESS ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	const currentState: ReportExecutionState = exportStateQuery.data?.execution.state ?? initialState;
	const reportPeriod = exportStateQuery.data?.report
		? MarketStateExportUtilities.formatReportPeriod(exportStateQuery.data.report)
		: undefined;

	useEffect(() => {
		if (isInitialStatePending
			&& !hasNotifiedStateChange.current
			&& currentState !== ReportExecutionState.NEW
			&& currentState !== ReportExecutionState.IN_PROCESS) {
			hasNotifiedStateChange.current = true;
			onStateChange();
		}
	}, [currentState, isInitialStatePending, onStateChange]);

	return <li>
		<span className="mr-3">{reportPeriod ?? 'Market state export'}</span>
		{currentState === ReportExecutionState.NEW && <Tag state="warning">Pending</Tag>}
		{currentState === ReportExecutionState.IN_PROCESS && <Tag state="warning">Processing</Tag>}
		{currentState === ReportExecutionState.SUCCESS &&
			<a className="hover:text-blue-900 cursor-pointer underline" onClick={() => download(reportExecutionId)}>Download</a>}
		{currentState === ReportExecutionState.FAILED && <Tag state="critical">Failed</Tag>}
	</li>;
};

const formatParityPeriod = (period: ParityExportReportPeriod): string => {
	switch (period) {
		case ParityExportReportPeriod.CURRENT_MONTH:
			return 'Current month';
		case ParityExportReportPeriod.MONTHS_3:
			return 'Next 3 months';
		case ParityExportReportPeriod.MONTHS_6:
			return 'Next 6 months';
		case ParityExportReportPeriod.MONTHS_12:
			return 'Next 12 months';
		default:
			return 'Parity export';
	}
};

const RenderExportParityNotifications: React.FunctionComponent<{
	notifications: SiteNotification[],
	acknowledgeAll: (ids: number[]) => void
}> = ({ notifications, acknowledgeAll }) => {

	const { companyHotel } = useSelectedCompanyHotel()!;
	const toaster = useToaster();
	const siteNotificationsTools = useSiteNotificationTools();

	const download = (reportExecutionId: number) => {
		ParityExportService.findCompanyHotelParityExportReportExecution(companyHotel.id, reportExecutionId)
			.then(exp => ParityExportService.download(exp))
			.catch(() => toaster.error('Download Parity Report', 'Error downloading export. Please try again later!'));
	};

	const map: Map<number, ExportMarketStateNotificationProperties> = new Map();

	notifications.forEach(n => {
		const reportExecutionId = resolveReportExecutionId(n.properties);
		if (reportExecutionId !== undefined) {
			map.set(reportExecutionId, n.properties);
		}
	});

	if (_isEmpty(map)) return null;

	return <Message state="info" onDismiss={() => acknowledgeAll(notifications.map(n => n.id))}
								title="The below parity reports are in the process to be created. You will be notified when they are ready to be downloaded.">
			<div className="mt-2 text-sm text-blue-700">
				<ul role="list" className="list-disc space-y-1 pl-5">
					{Array.from(map).map(([reportExecutionId, properties]) =>
						<RenderExportParityNotificationItem
							key={reportExecutionId}
							companyHotelId={companyHotel.id}
							properties={properties}
							download={download}
							onStateChange={siteNotificationsTools.refetch}
						/>)}
				</ul>
			</div>
		</Message>;
};

const RenderExportParityNotificationItem: React.FunctionComponent<{
	companyHotelId: number,
	properties: ExportMarketStateNotificationProperties,
	download: (reportExecutionId: number) => void,
	onStateChange: () => void
}> = ({ companyHotelId, properties, download, onStateChange }) => {

	const initialState: ReportExecutionState = properties.state;
	const reportExecutionId = resolveReportExecutionId(properties)!;
	const isInitialStatePending = initialState === ReportExecutionState.NEW || initialState === ReportExecutionState.IN_PROCESS;
	const hasNotifiedStateChange = React.useRef(false);

	const exportStateQuery = useQuery<ParityExportReportExecution>({
		queryKey: ['site-notification-parity-export-execution', companyHotelId, reportExecutionId],
		queryFn: () => ParityExportService.findCompanyHotelParityExportReportExecution(companyHotelId, reportExecutionId),
		enabled: isInitialStatePending,
		refetchInterval: (query) => {
			const state = query.state.data?.execution.state ?? initialState;
			return state === ReportExecutionState.NEW || state === ReportExecutionState.IN_PROCESS ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	const currentState: ReportExecutionState = exportStateQuery.data?.execution.state ?? initialState;
	const reportPeriod = exportStateQuery.data?.report
		? formatParityPeriod(exportStateQuery.data.report.period)
		: undefined;

	useEffect(() => {
		if (isInitialStatePending
			&& !hasNotifiedStateChange.current
			&& currentState !== ReportExecutionState.NEW
			&& currentState !== ReportExecutionState.IN_PROCESS) {
			hasNotifiedStateChange.current = true;
			onStateChange();
		}
	}, [currentState, isInitialStatePending, onStateChange]);

	return <li>
		<span className="mr-3">{reportPeriod ?? 'Parity export'}</span>
		{currentState === ReportExecutionState.NEW && <Tag state="warning">Pending</Tag>}
		{currentState === ReportExecutionState.IN_PROCESS && <Tag state="warning">Processing</Tag>}
		{currentState === ReportExecutionState.SUCCESS &&
			<a className="hover:text-blue-900 cursor-pointer underline" onClick={() => download(reportExecutionId)}>Download</a>}
		{currentState === ReportExecutionState.FAILED && <Tag state="critical">Failed</Tag>}
	</li>;
};


export default SiteNotificationFragment;
